import axios from "axios";
import { rootStore } from "@/mobx/store";
import { ENV } from "@/config/env";
import { HEADER, RESULT_CODE } from "@/config/const";
import { refreshAccessToken, isTokenExpiringSoon } from "@/utils/tokenRefresh";

// 글로벌 로딩 상태 관리를 위한 변수
let activeRequests = 0;
const loadingSubscribers = new Set<(loading: boolean) => void>();

// 로딩 상태 변경 알림 함수
const notifyLoadingChange = (loading: boolean) => {
  loadingSubscribers.forEach((callback) => callback(loading));
};

// 로딩 상태 구독 함수
export const subscribeToLoading = (callback: (loading: boolean) => void) => {
  loadingSubscribers.add(callback);
  return () => loadingSubscribers.delete(callback);
};

// axios 인스턴스 생성
const api = axios.create({
  baseURL: ENV.API_URL.replace(/\/$/, ""),
  headers: {
    [HEADER.LANG]: "ko", // 언어 설정
  },
});

// 요청 전: 토큰 갱신 및 헤더에 추가
api.interceptors.request.use(async (config) => {
  // 로딩 상태 증가
  activeRequests++;
  notifyLoadingChange(true);

  try {
    // refresh endpoint가 아닌 경우에만 토큰 갱신 시도
    if (!config.url?.includes("/auth/refresh")) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        const currentToken = rootStore.token;

        // 토큰이 없거나 곧 만료될 예정이면 갱신
        if (!currentToken || isTokenExpiringSoon(currentToken)) {
          const newToken = await refreshAccessToken();
          config.headers[HEADER.AUTH_TOKEN] = newToken;
        } else {
          // 기존 토큰 사용
          config.headers[HEADER.AUTH_TOKEN] = currentToken;
        }
      } else {
        // 기존 토큰 사용
        const token = rootStore.token;
        if (token) {
          config.headers[HEADER.AUTH_TOKEN] = token;
        }
      }
    }
  } catch (error) {
    console.error(error);
    // 토큰 갱신 실패 시 기존 토큰 사용
    const token = rootStore.token;
    if (token) {
      config.headers[HEADER.AUTH_TOKEN] = token;
    }
  }

  return config;
});

// 응답 에러 처리 (토큰 만료 대응)
api.interceptors.response.use(
  (res) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }
    return res;
  },
  async (err) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }

    const originalRequest = err.config;

    // 토큰 만료 시 재요청 로직
    if (
      err.response?.data?.result_code === RESULT_CODE.TOKEN_EXPIRED &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers[HEADER.AUTH_TOKEN] = newToken;
        return api(originalRequest);
      } catch (e) {
        // refresh 실패 시 로그아웃 처리
        rootStore.signOut();
        // window.location.href 대신 navigate 사용 권장
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(e);
      }
    }

    // 인증 관련 에러 처리 (토큰이 없는 경우만)
    if (err.response?.data?.result_code === RESULT_CODE.EMPTY_TOKEN) {
      rootStore.signOut();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

export default api;
